import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {DcpPriceCommitmentPeriodData} from '@app/model/price/dcp-price-commitment-period-data';
import {DcpPriceIncludesData} from '@app/model/price/dcp-price-includes-data';
import {DcpPriceRatePlansData} from '@app/model/price/dcp-price-rate-plans-data';
import {DcpPriceRestrictionsData} from '@app/model/price/dcp-price-restrictions-data';
import {DcpPriceServicePlanData} from '@app/model/price/dcp-price-service-plan-data';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {DcpCurrencyService} from '@app/services/pricing/dcp-currency.service';
import {DcpBundleTemplate, DcpGlobalMessageService, DcpPriceDisplayData, DcpPriceDisplayOptionsData, TransactionListCta, ProductDetermination} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Product} from '@spartacus/core/src/model/product.model';
import {CurrentProductService} from '@spartacus/storefront';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {DcpProductUtilitiesService} from '../../../services/dcp-product-utilities.service';
import {ProductPageDecoratorService} from '../../../services/product-page-decorator.service';
import {NavigationLink} from '../../navigation-bar/navigation-link';
import {PlansAndPricingDataService} from '../plans-and-pricing-data.service';

@UntilDestroy()
@Component({
  selector: 'dcp-plans-and-pricing-bundle-option',
  templateUrl: './plans-and-pricing-bundle-option.component.html',
})
export class PlansAndPricingBundleOptionComponent implements OnInit {

  servicePlan: DcpPriceServicePlanData;
  includes: DcpPriceIncludesData[];
  commitmentPeriod: DcpPriceCommitmentPeriodData;
  restrictions: DcpPriceRestrictionsData[];
  priceDisplay: DcpPriceDisplayData;
  ratePlans: DcpPriceRatePlansData[] = [];
  priceDisplayOptions: DcpPriceDisplayOptionsData;

  navLink: NavigationLink = {
    headline: 'productDetails.plansAndPricing',
    id: 'plans-and-pricing-section'
  };
  private internalBundleTemplate: DcpBundleTemplate;

  constructor(
    private cdr: ChangeDetectorRef,
    private currentProductService: CurrentProductService,
    private plansAndPricingDataService: PlansAndPricingDataService,
    private productPageDecoratorService: ProductPageDecoratorService,
    private productUtilitiesService: DcpProductUtilitiesService,
    private countrySelectorService: CountrySelectorService,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private currencyService: DcpCurrencyService,
    private configService: DcpConfigService) {
  }

  get bundleTemplate(): DcpBundleTemplate {
    return this.internalBundleTemplate;
  }

  @Input() set bundleTemplate(template: DcpBundleTemplate) {
    this.internalBundleTemplate = template;
    if (template?.visible) {
      this.servicePlan = this.plansAndPricingDataService.getServicePlans(template.data);
      this.includes = this.plansAndPricingDataService.getIncludes(template.data);
      this.commitmentPeriod = this.plansAndPricingDataService.getCommitmentPeriod(template.data);
      this.restrictions = this.plansAndPricingDataService.getRestrictions(template.data);
      this.ratePlans = this.plansAndPricingDataService.getRatePlans(template.data);
      this.priceDisplayOptions = this.plansAndPricingDataService.getPriceDisplayOptions(template.data);
      this.setPriceDisplay(template);
    }
  }

  get hasContentForFeatureSection(): boolean {
    return (!!this.servicePlan && !!this.servicePlan.features && !!this.servicePlan.features.length)
      || (!!this.includes && !!this.includes.length)
      || (!!this.restrictions && !!this.restrictions.length)
      || (!!this.commitmentPeriod);
  }

  ngOnInit(): void {
    this.countrySelectorService.getChangeNotification()
      .pipe(untilDestroyed(this))
      .subscribe(() => this.setPriceDisplay(this.bundleTemplate));
  }

  isVisible(): boolean {
    return this.bundleTemplate?.visible !== false;
  }

  setPriceDisplay(template: DcpBundleTemplate): void {
    this.currencyService.getActive().pipe(untilDestroyed(this)).subscribe(activeCurrency => {
      this.getPriceDisplay(template, activeCurrency).pipe(untilDestroyed(this)).subscribe(pd => {
        this.priceDisplay = pd;
        this.cdr.detectChanges();
      });
    });
  }

  /**
   * Gets information that is missing in the bundle templates from the CtaProductDetermination-Api or the
   * PriceCalculation-Api and combines its return values with priceDisplay.
   *
   * @param template Current bundleTemplate from the product.
   * @param activeCurrency current active currency for caching
   */
  private getPriceDisplay(template: DcpBundleTemplate, activeCurrency: string): Observable<DcpPriceDisplayData> {
    const priceDisplay: DcpPriceDisplayData = this.plansAndPricingDataService.getPriceDisplay(template?.data);
    return this.currentProductService.getProduct().pipe(
      switchMap(product => {
        let pd$: Observable<DcpPriceDisplayData> = of(priceDisplay);

        return this.configService.getCommercialModelConfig(product, null, template).pipe(
          switchMap(commercialModelConfig => {
            if (!commercialModelConfig.skipPricingService && this.productUtilitiesService.isSapProduct(product)) {
              pd$ = this.createPriceDisplayFromPdpService(template, product, activeCurrency);
            }
            return pd$.pipe(
              switchMap(pd => this.setPriceDisplayCtas(template, product, pd))
            );
          })
        );
      })
    );
  }

  /**
   * Creates a priceDisplay like object with data from the ctas and price items for this bundle.
   * The ctas are fetched from the pdpDecorator/price-calculation service.
   *
   * @param template current Bundle Template
   * @param product current Product
   * @param activeCurrency current active currency for caching
   */
  private createPriceDisplayFromPdpService(template: DcpBundleTemplate, product: Product, activeCurrency: string): Observable<DcpPriceDisplayData> {
    return this.productPageDecoratorService.getPriceCalculation(template?.supplierAlternativeAID, product, template, activeCurrency).pipe(
      map(priceItem => this.productPageDecoratorService.convertToPriceDisplay(
        priceItem?.items
      )),
      catchError((e) => {
        this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
        const priceDisplay: DcpPriceDisplayData = this.productPageDecoratorService.buildDcpPriceDisplayData();
        priceDisplay.labels.main = '';
        return of(priceDisplay);
      })
    );
  }

  /**
   * Merges the current priceDisplay with the ctas for the bundle.
   * The ctas are fetched from the pdpDecorator/cta-product-determination service.
   *
   * @param template current Bundle Template
   * @param priceDisplay priceDisplay of current Bundle Template
   * @param product current Product
   */
  private setPriceDisplayCtas(
    template: DcpBundleTemplate,
    product: Product,
    priceDisplay: DcpPriceDisplayData = {},
  ): Observable<DcpPriceDisplayData> {
    return this.productPageDecoratorService.getCtaProductDetermination(product).pipe(
      map(ctaProductDetermination => {
        if (!priceDisplay.labels) {
          priceDisplay.labels = {main: '', tooltip: false};
        }
        if (ctaProductDetermination) {
          priceDisplay.labels.ctas = this.getPriceDisplayCtas(template, ctaProductDetermination);
        } else {
          priceDisplay.labels.ctas = [{
            code: 'CONTACT_SUPPORT',
            name: '',
            label: 'productDetails.contactSupport',
          }];
        }
        return priceDisplay;
      })
    );
  }

  private getPriceDisplayCtas(
    template: DcpBundleTemplate, ctaProductDetermination: ProductDetermination
  ): TransactionListCta[] {
    return this.productPageDecoratorService.getEntitlementFromProductDeterminationCta(
      ctaProductDetermination, template
    )?.callToActions;
  }
}
